import { getMedContent, MedFormat } from "src/utils/medContent/medContent";
import { FEATURE_PARAM } from "src/utils/medFeatureParams";
import { MED_MEMBERSHIP_FACADE } from "src/utils/medMembership";

// Map (not a plain object) so an arbitrary `?feature=` value like
// "constructor" can't hit an inherited property.
const FACADE_BY_FEATURE_PARAM = new Map<string, MED_MEMBERSHIP_FACADE>([
  [
    FEATURE_PARAM.MICRODOSE_WEGOVY_PILL,
    MED_MEMBERSHIP_FACADE.MICRODOSE_WEGOVY_PILL,
  ],
  [FEATURE_PARAM.WEGOVY_PILL, MED_MEMBERSHIP_FACADE.WEGOVY_PILL],
  // Live campaigns still send the old `wegovypill` spelling (see FEATURE_PARAM).
  [FEATURE_PARAM.WEGOVY_PILL_DEPRECATED, MED_MEMBERSHIP_FACADE.WEGOVY_PILL],
  [FEATURE_PARAM.WEGOVY_PEN, MED_MEMBERSHIP_FACADE.WEGOVY_PEN],
  [FEATURE_PARAM.FOUNDAYO, MED_MEMBERSHIP_FACADE.FOUNDAYO_PILL],
  [FEATURE_PARAM.ZEPBOUND_KWIKPEN, MED_MEMBERSHIP_FACADE.ZEPBOUND_KWIKPEN],
]);

/** The facade a `?feature=` value maps to, if any (non-facade flags map to none). */
export function getFacadeFromFeatureParam(
  feature: string | undefined
): MED_MEMBERSHIP_FACADE | undefined {
  return feature ? FACADE_BY_FEATURE_PARAM.get(feature) : undefined;
}

/**
 * `pill` or `injection` for a specific med, read from the med catalog so it
 * can't drift from `medicationFormat` answer values. `DEFAULT` has none.
 */
export function getMedicationFormatForFacade(
  facade: MED_MEMBERSHIP_FACADE
): MedFormat | undefined {
  return getMedContent(facade)?.format;
}

/**
 * Orders membership preference options for display: the facade the user came
 * in on first, then every med matching their `medicationFormat` answer, then
 * the rest. Stable, so config order is kept inside each group and options
 * with no format (e.g. "another option") stay last. Returns a copy.
 */
export function sortMembershipOptions<T extends { key: string }>(
  options: readonly T[],
  { facade, format }: { facade?: MED_MEMBERSHIP_FACADE; format?: string }
): T[] {
  const rank = ({ key }: T) => {
    if (facade !== undefined && key === facade) {
      return 0;
    }
    const optionFormat = getMedicationFormatForFacade(
      key as MED_MEMBERSHIP_FACADE
    );
    return optionFormat !== undefined && optionFormat === format ? 1 : 2;
  };
  return [...options].sort((a, b) => rank(a) - rank(b));
}
